import type { ReactNode } from 'react'

/**
 * The form furniture of the mockup's sheets, as its own stylesheet sizes them.
 *
 * Written as literals from the reference rather than as this app's shorthand,
 * for the reason §49 cost three rounds to learn: a measurement guessed from a
 * screenshot is a measurement wrong. These are `label.f` and its input --
 * 12px label, 7px gap, 48px control, radius 8, 14px text -- plus `.two`,
 * `.picker`/`.pv`, `.days`, `.upl` and `.slots`.
 *
 * PlaceSelect already carries the same numbers for its own <select>; these are
 * the rest of the set, so a form built from them matches the one beside it.
 */

export function Field({ label, hint, first = false, children }: {
  label: string
  hint?: string
  /** No top margin: label.f's 16px sits BETWEEN fields, not above the first. */
  first?: boolean
  children: ReactNode
}) {
  return (
    <label className={`block text-[12px] text-ink-2 ${first ? '' : 'mt-4'}`}>
      {label}
      {hint && <span className="text-ink-3"> {hint}</span>}
      {children}
    </label>
  )
}

const CONTROL = `mt-[7px] block min-h-[48px] w-full rounded-lg border border-line
                 bg-surface px-[13px] py-3 text-[14px] text-ink
                 placeholder:text-ink-3 focus:ring-2 focus:ring-accent`

export function Text({ value, onChange, placeholder, mono = false, type = 'text', inputMode }: {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  mono?: boolean
  type?: 'text' | 'time' | 'date' | 'password' | 'tel' | 'email'
  inputMode?: 'numeric' | 'decimal' | 'tel'
}) {
  return (
    <input
      type={type}
      value={value}
      inputMode={inputMode}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${CONTROL} ${mono ? 'font-mono' : ''}`}
    />
  )
}

export function Area({ value, onChange, placeholder }: {
  value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <textarea
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${CONTROL} min-h-[84px] resize-y`}
    />
  )
}

export function Select({ value, onChange, children }: {
  value: string; onChange: (v: string) => void; children: ReactNode
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
            className={`${CONTROL} pe-9`}>
      {children}
    </select>
  )
}

/** `.two` — two controls on one line, 10px apart, neither carrying a margin. */
export function TwoUp({ children }: { children: ReactNode }) {
  return <div className="mt-4 grid grid-cols-2 gap-2.5 [&>label]:mt-0">{children}</div>
}

/** `.picker` / `.pv` — a small grid of pressed-state choices. */
export function Picker<T extends string>({ value, onChange, options, cols = 2 }: {
  value: T | null
  onChange: (v: T) => void
  options: { value: T; label: string; sub?: string; art?: ReactNode }[]
  cols?: 2 | 3
}) {
  return (
    <div className={`mt-[9px] grid gap-2 ${cols === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
      {options.map((o) => (
        <button
          key={o.value} type="button"
          aria-pressed={value === o.value}
          onClick={() => onChange(o.value)}
          className={`rounded-[9px] border p-2 text-center text-[11px] font-[650]
                      ${value === o.value
                        ? 'border-accent bg-accent-wash text-accent'
                        : 'border-line bg-surface text-ink'}`}
        >
          {o.art}
          {o.label}
          {o.sub && <span className="mt-0.5 block text-[10px] font-normal text-ink-3">{o.sub}</span>}
        </button>
      ))}
    </div>
  )
}

/**
 * `.days` — the seven-day strip.
 *
 * ISO weekdays, 1 = Monday, because that is what scheduleCreateSchema's
 * `weekdays` validates (1..7) and what expandScheduleToTrips counts in. The
 * mockup's own data-day was 0-based and would have shifted every departure by
 * a day.
 */
export function DayPicker({ value, onChange, labels }: {
  value: number[]
  onChange: (v: number[]) => void
  labels: string[]
}) {
  return (
    <div className="mt-[7px] grid grid-cols-7 gap-[5px]">
      {labels.map((d, i) => {
        const day = i + 1
        const on = value.includes(day)
        return (
          <button
            key={day} type="button" aria-pressed={on}
            onClick={() => onChange(on ? value.filter((x) => x !== day) : [...value, day].sort())}
            className={`min-h-[36px] rounded-[7px] border text-[10.5px] font-[650]
                        ${on ? 'border-accent bg-accent text-white'
                             : 'border-line bg-surface text-ink-3'}`}
          >{d}</button>
        )
      })}
    </div>
  )
}

/** The amber line a sheet shows when the server refused it. */
export function FormError({ children }: { children: ReactNode }) {
  if (!children) return null
  return (
    <p className="mt-3 rounded-lg border border-band-red/40 bg-band-red-wash px-3 py-2.5
                  text-[11px] leading-relaxed text-band-red">
      {children}
    </p>
  )
}

/** The sheet's own save button, 44px as `.btn`, with its busy state. */
export function SaveBar({ label, busy, disabled, onSave, note }: {
  label: string
  busy?: boolean
  disabled?: boolean
  onSave: () => void
  note?: string
}) {
  return (
    <div className="mt-5">
      <button
        type="button" onClick={onSave} disabled={busy || disabled}
        className="min-h-[44px] w-full rounded-lg bg-accent px-3.5 text-[13px] font-[650]
                   text-white disabled:opacity-40"
      >
        {busy ? '…' : label}
      </button>
      {note && <p className="mt-[9px] text-center text-[11px] text-ink-3">{note}</p>}
    </div>
  )
}
